/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  ButtonProps,
  Container,
  Dialog,
  Drawer,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { WalletCreatedData, walletEvents } from '../../../../events/walletEvents';
import useIsMobile from '../../../../hooks/ui-states/useIsMobile';
import { SlideTransition } from '../../../../utils/transitions';
import MainAppBar from '../../../global/MainAppBar';
import CircleButton from '../../../ui/CircleButton';
import { PlusIcon } from '../../../ui/icons';
import CreateWalletTypeSelector from '../CreateWalletTypeSelector';

interface ButtonCreateWalletProps extends ButtonProps {
  circleButton?: boolean;
  hideText?: boolean;
  renderFormOnPopup?: boolean;
  textColor?: string;
}

const ButtonCreateWallet = forwardRef<
  HTMLButtonElement,
  ButtonCreateWalletProps
>((props, ref) => {
  const { children, circleButton = true, color = 'primary', textColor, renderFormOnPopup = false, ...rest } = props;
  const theme = useTheme();
  const { t } = useTranslation(['common', 'assets']);
  const isMobile = useIsMobile();
  const isOpen = window.location.search.includes('create-wallet');
  const [open, setOpen] = useState(isOpen || false);
  const navigate = useNavigate();
  const location = useLocation();

  const { setPageBackground } = useThemeContext();


  useEffect(() => {
    if (open) {
      setPageBackground(theme.palette.common.white);
      navigate(location.pathname, {
        state: {
          createWallet: true,
        },
        replace: true,
      });
    } else {
      setPageBackground(theme.palette.pink[500]);
      navigate(location.pathname, {
        state: {
          createWallet: false,
        },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, navigate, location.pathname]);

  useEffect(() => {
    const unsubscribe = walletEvents.subscribe('wallet:created', (_data: WalletCreatedData) => {
      if (renderFormOnPopup) {
        setOpen(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const renderContent = () => {
    if (isMobile) {
      return (
        <Dialog
          open={open}
          fullScreen={isMobile}
          slots={{ transition: isMobile ? SlideTransition : undefined }}
          onClose={() => setOpen(!open)}
          scroll="body"
          slotProps={{
            paper: {
              sx: { maxWidth: theme.maxContent },
            },
          }}
        >
          <MainAppBar
            preventGoBack
            beforeGoBack={() => setOpen(false)}
            title={t('create_wallet_title', { ns: 'assets' })}
          />
          <Box>
            <Container>
              <Stack paddingTop={14} spacing={4} textAlign="center">
                <CreateWalletTypeSelector renderFormOnPopup={renderFormOnPopup} />
              </Stack>
            </Container>
          </Box>
        </Dialog>
      );
    }

    return (
      <Drawer
        open={open}
        anchor="right"
        onClose={() => setOpen(!open)}
        sx={{ zIndex: theme.zIndex.modal + 1 }}
      >
        <Box sx={{ width: 400, paddingTop: 12 }}>
          <Container>
            <CreateWalletTypeSelector renderFormOnPopup={renderFormOnPopup} />
          </Container>
        </Box>
      </Drawer>
    );
  };

  if (!circleButton) {
    return (
      <>
        <Button
          {...rest}
          ref={ref}
          onClick={() => setOpen(!open)}
          startIcon={<PlusIcon fontSize={24} />}
          sx={{ color: textColor, ...(rest.sx || {}) }}
        >
          {children}
        </Button>
        {renderContent()}
      </>
    );
  }

  return (
    <>
      <Stack spacing={1} justifyContent="center" alignItems="center">
        <CircleButton
          color={color}
          onClick={() => setOpen(!open)}
          aria-label="create wallet"
          size={32}
          textColor={textColor}
        >
          <PlusIcon fontSize={40} />
        </CircleButton>

        {!rest.hideText && (
          <Typography variant="h3" color="textSecondary" fontWeight={700}>
            {t('create_wallet_start', { ns: 'assets' })}
          </Typography>
        )}
      </Stack>
      {renderContent()}
    </>
  );
});

export default ButtonCreateWallet;
